import { PortableText } from '@portabletext/react';
import { Body, H2, H3, PrimaryButton, Tag } from '@rikstv/shared-components';
import { useEffect, useRef } from 'react';
import { trackInternetSalesView } from '~/analytics/eventTracker';
import { Card, Icon, Layout, Loader } from '~/components/shared';
import { FlexBox } from '~/components/shared/layout/FlexBox';
import { SupportFooter } from '~/components/shared/support-footer/SupportFooter';
import classes from './InternetLandingPage.module.css';
import { AddressSearchInput } from './components/AddressSearchInput';
import { useAddressSearch } from './hooks/useAddressSearch';
import { useLazySanityData } from './hooks/useLazySanityData';
import { useInternetFlowStore } from './useInternetStore';

export const Route_InternetLandingPage = 'internett';

const INPUT_ID = 's-input';

let doReset = true;
const resetStoreStateOnceOnLoad = () => {
  if (doReset) {
    doReset = false;
    useInternetFlowStore.getState().resetState();
  }
};

export const InternetLandingPage = () => {
  resetStoreStateOnceOnLoad();
  useEffect(() => trackInternetSalesView(), []);
  const { data, isLoading, setSearchString, selectedAddress, onSelectAutocompleteSuggestion, onClickCheckAvailability } =
    useAddressSearch();
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const {
    displayData: { title, description, image },
  } = useLazySanityData();
  useEffect(() => {
    // hide and show search results panel
    if (searchResultsRef.current) {
      if (data) {
        searchResultsRef.current.style.display = 'block';
      } else {
        searchResultsRef.current.style.display = 'none';
        // try-focus on the input
        (document.getElementById(`${INPUT_ID}`) as HTMLInputElement | null)?.focus();
      }
    }
  }, [data]);

  return (
    <>
      <Layout wide>
        <div className={classes.grid}>
          <div className={classes.imageContainer}>
            <img src={`${image}?auto=format&w=1200`} alt="" />
          </div>
          <Card noMargin>
            <FlexBox direction="column" gap="calc(1.5rem + var(--rds-fluid-spacing--0-24))">
              <Tag className={classes.tag}>— TV og underholdning via internett</Tag>
              <FlexBox direction="column">
                <H2>{title}</H2>
                <Body as="div" className={classes.portableText}>
                  <PortableText value={description} />
                </Body>
              </FlexBox>
              <Card color="gray-dark">
                <section>
                  <H3>Sjekk tilgjengelighet der du bor</H3>
                  <br />
                  <div className={classes.searchWrapper}>
                    <AddressSearchInput
                      id={INPUT_ID}
                      defaultValue={selectedAddress?.description ?? ''}
                      onChange={(e) => {
                        setSearchString(e.target.value);
                      }}
                    />
                    <div id="search-results" ref={searchResultsRef} className={classes.searchResults}>
                      {isLoading && (
                        <div style={{ paddingBlock: '1rem' }}>
                          <Loader />
                        </div>
                      )}
                      {!isLoading && data && (
                        <ul onKeyDown={handleArrowKeyNavigationInPopupList} data-testid="address-search-results">
                          {data.installationAddresses.length > 0 &&
                            data.installationAddresses.map((addr) => (
                              <li key={addr.description}>
                                <button onClick={() => onSelectAutocompleteSuggestion(addr)}>{addr.description}</button>
                              </li>
                            ))}
                          {data.installationAddresses.length === 0 && <li style={{ padding: '.5rem 1rem' }}>Ingen resultater...</li>}
                        </ul>
                      )}
                    </div>
                  </div>
                  <br />
                  <PrimaryButton
                    data-testid="check-availability-button"
                    onClick={onClickCheckAvailability}
                    className={classes.availabilityButton}
                    disabled={!selectedAddress}
                  >
                    Sjekk tilgjengelighet
                    <Icon
                      type="search"
                      color="white"
                      size={1.5}
                      style={{ fill: selectedAddress ? 'white' : 'var(--common-button--disabled-color)' }}
                    />
                  </PrimaryButton>
                </section>
              </Card>
            </FlexBox>
          </Card>
        </div>
      </Layout>
      <SupportFooter />
    </>
  );
};

const handleArrowKeyNavigationInPopupList: React.KeyboardEventHandler = (e) => {
  // only handle vertical arrow keys
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
  // opt out when meta keys are pressed
  if (!(e.target instanceof HTMLElement) || e.altKey || e.shiftKey || e.ctrlKey || e.metaKey) return;
  // opt out when active element is not inside of the popover
  if (!e.target.contains(document.activeElement)) return;

  const liElement = document.activeElement?.parentElement;
  const nextElement = (e.key === 'ArrowDown' ? liElement?.nextElementSibling : liElement?.previousElementSibling)?.querySelector('button');
  if (nextElement) nextElement.focus();
};
